import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { api } from "@/modules/api";
import { logger } from "@/modules/logger";
import { objectModule } from "@/modules/object";
import { BaseError } from "@/domains/errors";
import { toastModule } from "@/modules/toast";
import { atlasRoutes } from "@/app/router/routes";
import { atlasAuthModule } from "@/modules/atlas-auth";
import { atlasAssetTokenModule } from "@/modules/atlas-asset-token";
import { Uuid } from "@/domains/global/identifiers";

export interface AuthContextType {
  authToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  logOut: () => Promise<void>;
  authenticateAccountUsingGoogleOAuthCode: (params: {
    code: string;
    redirectURI: string;
  }) => Promise<void>;
  authenticateAccountUsingEmailPassword: (params: {
    email: string;
    password: string;
  }) => Promise<void>;
}

const AtlasAuthContext = createContext<AuthContextType | null>(null);

interface AtlasAuthProviderProps {
  children: React.ReactNode;
}

export const AtlasAuthProvider: React.FC<AtlasAuthProviderProps> = ({ children }) => {
  const [authToken, setAuthToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const initialize = async () => {
      setIsLoading(true);
      try {
        const accountInfo = await atlasAuthModule.getCurrentAuthInfo();

        if (accountInfo?.authToken) {
          setAuthToken(accountInfo.authToken);
          api.setAuthToken(accountInfo.authToken);
        }
      } catch (error) {
        logger.error({
          message: "Failed to initialize auth",
          info: { err: objectModule.safeErrorAsJson(error as BaseError) },
        });
      } finally {
        setIsLoading(false);
      }
    };

    initialize();
  }, []);

  const storeAuthToken = useCallback(
    async ({ accountId, authToken }: { accountId: Uuid; authToken: string }) => {
      try {
        await atlasAuthModule.setCurrentAuthInfo({
          info: { accountId, authToken },
        });

        setAuthToken(authToken);
        api.setAuthToken(authToken);
      } catch (error) {
        logger.warn({
          message: "Failed to persist auth token.",
          info: { err: objectModule.safeErrorAsJson(error as BaseError) },
        });
      }
    },
    []
  );

  const clearAuthToken = useCallback(async () => {
    try {
      await atlasAuthModule.clearCurrentAuthInfo();
      await atlasAssetTokenModule.clearToken();
      setAuthToken(null);
      api.setAuthToken(null);
    } catch (error) {
      logger.warn({
        message: "Failed to clear auth token.",
        info: { err: objectModule.safeErrorAsJson(error as BaseError) },
      });
    }
  }, []);

  const logOut = useCallback(async () => {
    await clearAuthToken();

    setTimeout(() => {
      window.location.replace("/");
    }, 300);
  }, [clearAuthToken]);

  const authenticateAccountUsingGoogleOAuthCode = useCallback(
    async ({ code, redirectURI }: { code: string; redirectURI: string }) => {
      setIsLoading(true);

      try {
        const result = await api.post("/v2/auth/google/log-in", {
          body: {
            google_oauth_code: code,
            redirect_uri: redirectURI,
            client_kind: "MEM_ATLAS_WEB_CLIENT",
          },
        });

        if (!result.data) {
          throw new BaseError({
            message: "Authentication failed.",
          });
        }

        const { account_id: authenticatedAccountId, oauth_access_token: token } = result.data;

        await storeAuthToken({
          accountId: authenticatedAccountId,
          authToken: token,
        });
      } catch (error) {
        toastModule.triggerToast({ content: "Failed to login." });

        logger.error({
          message: "Authentication failed",
          info: { err: objectModule.safeErrorAsJson(error as BaseError) },
        });

        setTimeout(() => window.location.assign(atlasRoutes.logIn({}).path), 500);
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  const authenticateAccountUsingEmailPassword = useCallback(
    async ({ email, password }: { email: string; password: string }) => {
      setIsLoading(true);

      try {
        const result = await api.post("/v2/auth/email-password/log-in", {
          body: {
            email_address: email,
            password: password,
            client_kind: "MEM_ATLAS_WEB_CLIENT",
          },
        });

        if (!result.data) {
          throw new BaseError({
            message: "Authentication failed.",
          });
        }

        const { account_id: authenticatedAccountId, oauth_access_token: token } = result.data;

        await storeAuthToken({
          accountId: authenticatedAccountId,
          authToken: token,
        });
      } catch (error) {
        toastModule.triggerToast({ content: "Failed to login." });

        logger.error({
          message: "Authentication failed",
          info: { err: objectModule.safeErrorAsJson(error as BaseError) },
        });

        setTimeout(() => window.location.assign(atlasRoutes.logIn({}).path), 500);
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  const value: AuthContextType = {
    authToken,
    isAuthenticated: Boolean(authToken),
    isLoading,
    logOut,
    authenticateAccountUsingGoogleOAuthCode,
    authenticateAccountUsingEmailPassword,
  };

  return <AtlasAuthContext.Provider value={value}>{children}</AtlasAuthContext.Provider>;
};

export const useAtlasAuthContext = (): { authContext: AuthContextType } => {
  const context = useContext(AtlasAuthContext);

  if (context === null) {
    throw new Error("useAtlasAuth must be used within an AtlasAuthProvider");
  }

  return { authContext: context };
};
